import type { ISODateTime } from "@albertine/types";
import {
    addHours,
    addMinutes,
    differenceInCalendarDays,
    differenceInMinutes,
    differenceInSeconds,
    isDate,
    isSameMonth,
    isSameYear,
    isToday,
    isValid,
    isYesterday,
    parse,
    parseISO,
} from "date-fns";
import { format, fromZonedTime, getTimezoneOffset, toZonedTime } from "date-fns-tz";
import { type GeoPoint, Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { FirebaseTimestamp } from "../common/types/FirebaseTimestamp";
import { isString, zeroPad } from "../common/utils/string.util";
import { formatDefaultDate } from "../common/utils/time.formatted.util";
import {
    dateWithShortMonthWithTimeFormat,
    LocalDate,
    LocalTime,
} from "../common/utils/time.util";
import config from "../config";
import { isFirebaseTimestamp, toTimestamp } from "./timestamp.util";

const hourInMilliseconds = 3600000;
const hourInMinutes = 60;
const dayInMinutes = hourInMinutes * 24;
const weekInMinutes = dayInMinutes * 7;

export const defaultTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
export const defaultDateFormat = "dd/MM/yyyy";
export const defaultTimeFormat = "HH:mm";
const dayDateFormat = "EEE d MMM yyyy";
export const dateWithShortMonthFormat = "dd MMM yyyy";

export const defaultDateTimeFormat = `${defaultDateFormat}, ${defaultTimeFormat}`;

export function toDateTimeString(d: Date): string {
    return format(d, defaultDateTimeFormat);
}

export function timestampFromDateTimeStrings(
    date: string,
    time: string | null | undefined,
    defaultTime = "12:00",
): Timestamp {
    return Timestamp.fromMillis(Date.parse(`${date}T${time || defaultTime}`));
}

export const TimeString = z
    .string()
    .refine((s) => /^[012]?[0-9]:[0-5][0-9]$/.test(s))
    .transform((s) => {
        const [hour, minute] = s.split(":");
        return { hour: Number(hour), minute: Number(minute) };
    })
    .refine(
        (time) =>
            time.hour >= 0 && time.hour <= 23 && time.minute >= 0 && time.minute <= 59,
    );
export type TimeString = z.infer<typeof TimeString>;

export const ServiceHoursString = z
    .string()
    .transform((s) => {
        const [opening, closing] = s
            .trim()
            .split("-")
            .map((str) => str.trim());
        return { opening, closing };
    })
    .refine((oh) => {
        const opening = TimeString.safeParse(oh.opening);
        const closing = TimeString.safeParse(oh.closing);
        return (
            opening.success &&
            closing.success &&
            opening.data.hour * 100 + opening.data.minute <
                closing.data.hour * 100 + closing.data.minute
        );
    })
    .transform((oh) => ({
        opening: TimeString.parse(oh.opening),
        closing: TimeString.parse(oh.closing),
    }));
export type ServiceHoursString = z.infer<typeof ServiceHoursString>;

export function serviceHoursToString(hours: ServiceHoursString): string {
    return `${hours.opening.hour}:${zeroPad(hours.opening.minute)} - ${
        hours.closing.hour
    }:${zeroPad(hours.closing.minute)}`;
}

export function changeDateTimezone(
    date: Date,
    fromTimezone: string,
    toTimezone: string,
) {
    return fromZonedTime(toZonedTime(date, toTimezone), fromTimezone);
}

export function getDateInDefaultZonedTime(date: Date | null, fromTimezone: string) {
    return date
        ? format(
              changeDateTimezone(date, fromTimezone, defaultTimezone),
              dateWithShortMonthWithTimeFormat,
          )
        : null;
}

export function toDateFromString(
    value: string | null | undefined,
    dateFormat: string,
): Date | null | undefined {
    if (!value) return undefined;
    const parsedISOValue = parseISO(value);
    if (isValid(parsedISOValue)) {
        return parse(value, dateFormat, new Date());
    }
    return undefined;
}

export function toDateTimeLocalInputValue(date: Date | null): string {
    return date ? format(date, "yyyy-MM-dd'T'HH:mm") : "";
}

export function combineDateAndTimeString(
    date: string | null | undefined,
    time: string | null | undefined,
) {
    return LocalDate.safeParse(date).success && LocalTime.safeParse(time).success
        ? `${date}T${time}`
        : null;
}

export function formatRequestDateTimeStrings(
    startDate: string | null | undefined,
    startTime: string | null | undefined,
    endDate: string | null | undefined,
    now: Date = new Date(),
    displayDayPrefix = false,
): string | null {
    const E = displayDayPrefix ? "E, " : "";

    if (startDate && endDate && startDate !== endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        const from = format(
            start,
            isSameYear(start, end)
                ? isSameMonth(start, end)
                    ? `${E}d`
                    : `${E}d MMM`
                : isSameYear(now, start)
                  ? `${E}d MMM`
                  : `${E}d MMM yyyy`,
        );

        const to = format(end, isSameYear(now, end) ? `${E}d MMM` : `${E}d MMM yyyy`);

        return `${from} - ${to}`;
    }

    const dateTime = combineDateAndTimeString(startDate, startTime);
    if (dateTime) {
        const start = new Date(dateTime);
        return format(
            start,
            isSameYear(now, start) ? `${E}d MMM HH:mm` : `${E}d MMM yyyy HH:mm`,
        );
    }

    const date = LocalDate.safeParse(startDate).success ? startDate : null;
    if (date) {
        const start = new Date(date);
        return format(start, isSameYear(now, start) ? `${E}d MMM` : `${E}d MMM yyyy`);
    }

    return null;
}

export function fromDateStringToZonedDate(zonedDate: string, timezone: string): Date {
    return toZonedTime(fromZonedTime(zonedDate, timezone), timezone);
}

export function gmtDifference(date: Date, timezone: string) {
    const hourDifference = getTimezoneOffset(timezone, date) / hourInMilliseconds;

    return `GMT${hourDifference < 0 ? "" : "+"}${hourDifference}:00`;
}

function toDateOnly(date: Date, timeZone: string): Date {
    return new Date(format(toZonedTime(date, timeZone), "yyyy-MM-dd", { timeZone }));
}

export function calculateNights(
    checkIn: Date,
    checkOut: Date,
    timeZone: string,
): number {
    const checkInDate = toDateOnly(checkIn, timeZone);
    const checkOutDate = toDateOnly(checkOut, timeZone);
    return differenceInCalendarDays(checkOutDate, checkInDate);
}

export function hoursFrom(now: Date, hours: number): Date {
    return addHours(now, hours);
}

export function minutesFrom(now: Date, minutes: number): Date {
    return addMinutes(now, minutes);
}

export function howLongUntilDate(
    now: Date,
    nextDate: Date,
): {
    quantity: number;
    timePeriod: "day" | "week" | "minute" | "hour" | "dateInMilliseconds";
} {
    const difference = differenceInMinutes(nextDate, now);

    if (difference < hourInMinutes) {
        return {
            quantity: Math.round(difference),
            timePeriod: "minute",
        };
    }

    if (difference < dayInMinutes) {
        return {
            quantity: Math.round(difference / 60),
            timePeriod: "hour",
        };
    }

    if (difference < weekInMinutes) {
        return {
            quantity: Math.round(difference / 60 / 24),
            timePeriod: "day",
        };
    }

    return {
        quantity: nextDate.getTime(),
        timePeriod: "dateInMilliseconds",
    };
}

type TimeUntilDeadline = {
    urgency: "overdue" | "priority_1" | "priority_2" | "priority_3" | "priority_4";
    label: string;
    quantity: number;
    timePeriod: "day" | "week" | "minute" | "hour" | "dateInMilliseconds";
};

export function showTimeUntilDeadline(
    now: Date,
    nextDeadlineAt: Date | undefined,
): TimeUntilDeadline | undefined {
    if (!nextDeadlineAt) {
        return undefined;
    }

    const isOverdue = nextDeadlineAt <= now;
    const { quantity, timePeriod } = isOverdue
        ? howLongUntilDate(nextDeadlineAt, now)
        : howLongUntilDate(now, nextDeadlineAt);

    const isQuantityMoreThanOne = quantity > 1;

    const priorityWithOverdue = (
        priority: "priority_1" | "priority_2" | "priority_3" | "priority_4",
    ) => (isOverdue ? `overdue` : priority);

    const labelWithPrefix = (
        label: string,
        priority: ReturnType<typeof priorityWithOverdue>,
    ) => {
        if (isOverdue) {
            return `Overdue, ${label}`;
        }

        if (priority !== "overdue") {
            if (timePeriod === "dateInMilliseconds") {
                return `Due by ${label}`;
            }
            return `Due in ${label}`;
        }

        return label;
    };

    switch (timePeriod) {
        case "minute": {
            const appendix = isQuantityMoreThanOne ? "minutes" : "minute";
            const urgency = priorityWithOverdue("priority_1");
            return {
                urgency,
                label:
                    quantity === 0
                        ? "Overdue"
                        : labelWithPrefix(`${quantity} ${appendix}`, urgency),
                quantity,
                timePeriod,
            };
        }
        case "hour": {
            const appendix = isQuantityMoreThanOne ? "hours" : "hour";

            if (quantity <= 8) {
                const urgency = priorityWithOverdue("priority_2");
                return {
                    urgency,
                    label: labelWithPrefix(`${quantity} ${appendix}`, urgency),
                    quantity,
                    timePeriod,
                };
            }

            const urgency = priorityWithOverdue("priority_3");
            return {
                urgency,
                label: labelWithPrefix(`${quantity} ${appendix}`, urgency),
                quantity,
                timePeriod,
            };
        }
        case "day": {
            const appendix = isQuantityMoreThanOne ? "days" : "day";
            const urgency = priorityWithOverdue("priority_4");
            return {
                urgency,
                label: labelWithPrefix(`${quantity} ${appendix}`, urgency),
                quantity,
                timePeriod,
            };
        }
        case "dateInMilliseconds": {
            const urgency = priorityWithOverdue("priority_4");
            return {
                urgency,
                label: labelWithPrefix(formatDefaultDate(nextDeadlineAt), urgency),
                quantity,
                timePeriod,
            };
        }

        default:
            return undefined;
    }
}

export type TimeZoneResponse = {
    dstOffset: number;
    rawOffset: number;
    status: string;
    timeZoneId: string | undefined;
    timeZoneName: string;
};

export async function getTimezoneByGeopoint(
    geoPoint: GeoPoint,
    timestamp: number = new Date().getTime() / 1000,
): Promise<TimeZoneResponse | null> {
    try {
        const location = encodeURIComponent(
            `${geoPoint.latitude},${geoPoint.longitude}`,
        );
        const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${location}&timestamp=${timestamp}&key=${config.mapsApiKey}`;

        const result = await fetch(url);
        const timezoneResponse = (await result.json()) as TimeZoneResponse;

        return timezoneResponse;
    } catch (error) {
        console.error(error);
        alert((error as Error).message);
        return null;
    }
}

function formatDateForChatMessage(date: Date): string {
    if (isDate(date)) {
        if (isToday(date)) return format(date, defaultTimeFormat);
        if (isYesterday(date)) return `Yesterday ${format(date, defaultTimeFormat)}`;
        return format(date, defaultDateTimeFormat);
    }
    return "";
}

export function formatIsoDateTimeForChatMessage(date: ISODateTime | null | undefined) {
    if (!date) {
        return "";
    }
    const dateObject = new Date(Date.parse(date));
    return formatDateForChatMessage(dateObject);
}

export function formatChatMessageDate(
    firebaseTimestamp: FirebaseTimestamp | null | undefined,
) {
    if (!firebaseTimestamp) return "";
    const date = toTimestamp(firebaseTimestamp).toDate();
    return formatDateForChatMessage(date);
}

export function formatChatHeaderDate(firebaseTimestamp: Timestamp | null) {
    if (!firebaseTimestamp) return "";
    const date = firebaseTimestamp.toDate();
    if (isDate(date)) {
        if (isToday(date)) return "Today";
        if (isYesterday(date)) return "Yesterday";
        return format(date, defaultDateFormat);
    }
    return "";
}

export function formatLocalDate(date: Date | null | undefined) {
    if (!date) return "";
    return format(date, defaultDateFormat);
}

export function formatLocalDateString(dateString: string) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isDate(date)) {
        return format(date, defaultDateFormat);
    }
    return "";
}

export const safelyConvertTime = (
    time: FirebaseTimestamp | string | null,
    timezone: string | null | undefined,
): string | null => {
    if (!time && !timezone) {
        return null;
    }

    if (isString(time) && isString(timezone)) {
        return format(toZonedTime(time, timezone), defaultDateTimeFormat);
    }

    if (isFirebaseTimestamp(time) && isString(timezone)) {
        return format(
            toZonedTime(toTimestamp(time).toDate(), timezone),
            defaultDateTimeFormat,
        );
    }

    return null;
};

export const getDeadlineTimeOptions = () => [
    {
        label: "30 minutes",
        value: minutesFrom(new Date(), 30),
        format: defaultTimeFormat,
    },
    {
        label: "1 hour",
        value: hoursFrom(new Date(), 1),
        format: defaultTimeFormat,
    },
    {
        label: "2 hours",
        value: hoursFrom(new Date(), 2),
        format: defaultTimeFormat,
    },
    {
        label: "4 hours",
        value: hoursFrom(new Date(), 4),
        format: defaultTimeFormat,
    },
];

export function isWithinNextMinute(time: Date, now: Date) {
    const diff = differenceInSeconds(time, now);
    const withinNextMinute = diff >= 0 && diff < 60;
    return withinNextMinute;
}

export function localDateToDayDateString(localDate: LocalDate): string {
    try {
        const parsed = parse(localDate, "yyyy-MM-dd", new Date());
        // default add 12:00:00 to the date to avoid timezone issues
        const dateWithLocalNoon = new Date(
            parsed.getFullYear(),
            parsed.getMonth(),
            parsed.getDate(),
            12,
            0,
            0,
        );
        if (!dateWithLocalNoon.getTime()) return "";
        return format(dateWithLocalNoon, dayDateFormat);
    } catch (error) {
        return "";
    }
}

export function dateWithLocalNoonTime(isoDateString: string): Date | null {
    const parsed = parse(isoDateString, "yyyy-MM-dd", new Date());
    if (isValid(parsed)) {
        // default add 12:00:00 to the date to avoid timezone issues
        return new Date(
            parsed.getFullYear(),
            parsed.getMonth(),
            parsed.getDate(),
            12,
            0,
            0,
        );
    }
    return null;
}
